import { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Search, Users, X } from "lucide-react";
import type {
  AdminDirectorySnapshot,
  DirectoryStaff,
  Duty,
  Role,
  Team,
  User,
} from "../shared/contracts";
import { request } from "./api";

type ProvisionRole = Extract<Role, "member" | "lead">;
const MAX_PROVISION = 100;

export function AdminDirectory({
  teams,
  users,
  onProvisioned,
  disabled,
}: {
  teams: Team[];
  users: User[];
  onProvisioned: () => Promise<void>;
  disabled: boolean;
}) {
  const [directory, setDirectory] = useState<AdminDirectorySnapshot | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"unlinked" | "all" | "linked">(
    "unlinked",
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [review, setReview] = useState(false);
  const [linkReview, setLinkReview] = useState<{
    source: DirectoryStaff;
    target: User;
  } | null>(null);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [role, setRole] = useState<ProvisionRole>("member");
  const [duty, setDuty] = useState<Duty>("other");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const reviewTrigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let active = true;
    request<AdminDirectorySnapshot>("GET", "/api/admin/directory")
      .then((value) => {
        if (active) setDirectory(value);
      })
      .catch((cause) => {
        if (active)
          setError(
            cause instanceof Error
              ? cause.message
              : "Could not load GHL staff.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (review) dialog?.showModal();
    return () => {
      dialog?.close();
      if (review) window.setTimeout(() => reviewTrigger.current?.focus(), 0);
    };
  }, [review]);
  const available = useMemo(
    () =>
      directory?.users.filter(
        (person) => person.present && !person.linkedUserId,
      ) ?? [],
    [directory],
  );
  const visible = useMemo(
    () =>
      (directory?.users ?? []).filter((person) => {
        if (filter === "unlinked" && (!person.present || person.linkedUserId))
          return false;
        if (filter === "linked" && !person.linkedUserId) return false;
        const locations =
          directory?.locations
            .filter((location) => person.locationIds.includes(location.id))
            .map((location) => location.name)
            .join(" ") ?? "";
        return `${person.name} ${person.email} ${locations}`
          .toLowerCase()
          .includes(query.trim().toLowerCase());
      }),
    [directory, filter, query],
  );
  const chosen = available.filter((person) => selected.includes(person.id));
  const existingMatch = (source: DirectoryStaff) => {
    if (!source.present || source.linkedUserId) return null;
    const matches = users.filter(
      (user) =>
        !user.ghlUserId &&
        user.email.trim().toLowerCase() === source.email.trim().toLowerCase(),
    );
    return matches.length === 1 ? matches[0] : null;
  };
  const link = async () => {
    if (!linkReview) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await request("POST", "/api/admin/directory/link", {
        ghlUserId: linkReview.source.id,
        userId: linkReview.target.id,
      });
      setLinkReview(null);
      setSelected((current) =>
        current.filter((id) => id !== linkReview.source.id),
      );
      setMessage(
        "Existing local account linked. Its role, duty, teams, and password were preserved.",
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not link account.",
      );
      setBusy(false);
      return;
    }
    try {
      const value = await request<AdminDirectorySnapshot>(
        "GET",
        "/api/admin/directory",
      );
      setDirectory(value);
      await onProvisioned();
    } catch {
      setError(
        "Account was linked, but the lists could not refresh. Reload to see the latest state.",
      );
    } finally {
      setBusy(false);
    }
  };
  const sync = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const value = await request<AdminDirectorySnapshot>(
        "POST",
        "/api/admin/directory/sync",
        {},
      );
      setDirectory(value);
      setSelected([]);
      setMessage(
        "GHL staff refreshed. Review the directory and setup status for access changes.",
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not refresh GHL staff.",
      );
    } finally {
      setBusy(false);
    }
  };
  const provision = async () => {
    if (!chosen.length || !teamIds.length) return;
    if (chosen.length > MAX_PROVISION) {
      setError(`Choose no more than ${MAX_PROVISION} staff per batch.`);
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await request<{ created: number; linked: number }>(
        "POST",
        "/api/admin/directory/provision",
        {
          ghlUserIds: chosen.map((person) => person.id),
          teamIds,
          role,
          duty,
        },
      );
      setSelected([]);
      setReview(false);
      setMessage(
        `${result.created} local account${result.created === 1 ? "" : "s"} added, ${result.linked} account${result.linked === 1 ? " was" : "s were"} already linked and unchanged. GHL staff was not changed.`,
      );
      try {
        const value = await request<AdminDirectorySnapshot>(
          "GET",
          "/api/admin/directory",
        );
        setDirectory(value);
        await onProvisioned();
      } catch {
        setError(
          "Accounts were added, but the lists could not refresh. Reload this page to see the latest state.",
        );
      }
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not add selected staff.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="directory-section" aria-labelledby="directory-title">
      <div className="management-head">
        <div>
          <h2 id="directory-title">GHL staff directory</h2>
          <p className="muted">
            Review GHL staff and linked GHL Connect accounts. Setup status shows
            whether automatic access is enabled. GHL locations define access;
            teams and call duties are configured here.
          </p>
        </div>
        <button
          type="button"
          className="secondary"
          disabled={busy || disabled || !directory?.configured}
          onClick={() => void sync()}
        >
          <RefreshCw size={16} />
          {busy ? "Working…" : "Refresh GHL"}
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {loading && <p className="muted">Loading GHL staff…</p>}
      {directory && !directory.configured && (
        <div className="empty-state">
          <Users size={22} />
          <strong>GHL directory is not configured</strong>
          <p>
            Ask a server administrator to configure GHL access. No API key is
            entered in the browser.
          </p>
        </div>
      )}
      {directory?.configured && (
        <>
          <div className="directory-meta">
            <span>
              {directory.demo ? "Fictional demo roster" : "GHL directory"}
            </span>
            <span>
              Last synced{" "}
              {directory.lastSyncAt
                ? new Date(directory.lastSyncAt).toLocaleString()
                : "never"}
            </span>
          </div>
          {directory.warnings.map((warning, index) => (
            <p
              className="directory-warning"
              key={`${index}-${warning}`}
              role="status"
            >
              {warning}
            </p>
          ))}
          <div className="directory-tools">
            <label className="search-field">
              <Search size={17} />
              <span className="sr-only">Search GHL staff</span>
              <input
                type="search"
                placeholder="Search name, email, or location"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <label className="filter-select">
              <span className="sr-only">Filter GHL staff</span>
              <select
                value={filter}
                onChange={(event) =>
                  setFilter(event.target.value as typeof filter)
                }
              >
                <option value="unlinked">Available to add</option>
                <option value="all">All staff</option>
                <option value="linked">Linked accounts</option>
              </select>
            </label>
            <button
              ref={reviewTrigger}
              type="button"
              disabled={disabled || busy || !selected.length}
              onClick={() => setReview(true)}
            >
              Review {selected.length} selected
            </button>
          </div>
          <p
            className="directory-limit"
            role={selected.length === MAX_PROVISION ? "status" : undefined}
          >
            Select up to {MAX_PROVISION} staff per batch.
            {selected.length === MAX_PROVISION
              ? " Limit reached; clear a selection to choose someone else."
              : ""}
          </p>
          <div className="directory-table" role="table" aria-label="GHL staff">
            <div className="directory-row directory-heading" role="row">
              <span role="columnheader">Select</span>
              <span role="columnheader">Staff member</span>
              <span role="columnheader">GHL locations</span>
              <span role="columnheader">Local account</span>
            </div>
            {visible.map((person) => (
              <div className="directory-row" role="row" key={person.id}>
                <span role="cell">
                  <input
                    type="checkbox"
                    aria-label={`Select ${person.name}`}
                    disabled={
                      busy ||
                      disabled ||
                      !person.present ||
                      !!person.linkedUserId ||
                      !!existingMatch(person) ||
                      (!selected.includes(person.id) &&
                        selected.length >= MAX_PROVISION)
                    }
                    checked={selected.includes(person.id)}
                    onChange={(event) =>
                      setSelected((current) =>
                        event.target.checked
                          ? current.length < MAX_PROVISION
                            ? [...current, person.id]
                            : current
                          : current.filter((id) => id !== person.id),
                      )
                    }
                  />
                </span>
                <span role="cell">
                  <strong>{person.name}</strong>
                  <small>{person.email}</small>
                </span>
                <span role="cell">
                  {directory.locations
                    .filter((location) =>
                      person.locationIds.includes(location.id),
                    )
                    .map((location) => location.name)
                    .join(", ") || "None listed"}
                </span>
                <span role="cell">
                  <span
                    className={`state-pill ${person.linkedUserId ? "linked" : ""}`}
                  >
                    {person.linkedUserId
                      ? "Linked"
                      : person.present
                        ? "Available"
                        : "No longer in GHL"}
                  </span>
                  {existingMatch(person) && (
                    <button
                      type="button"
                      className="text-button"
                      disabled={busy || disabled}
                      onClick={() =>
                        setLinkReview({
                          source: person,
                          target: existingMatch(person)!,
                        })
                      }
                    >
                      Link existing account
                    </button>
                  )}
                </span>
              </div>
            ))}
            {!visible.length && (
              <div className="empty-state">
                {!directory.lastSyncAt && !query
                  ? "Refresh GHL to load staff."
                  : query
                    ? "No staff match your search."
                    : filter === "unlinked"
                      ? "No unlinked GHL staff are available."
                      : "No staff in this view."}
              </div>
            )}
          </div>
        </>
      )}
      {review && (
        <dialog
          ref={dialogRef}
          className="desk-dialog directory-dialog"
          aria-label="Review local accounts"
          onCancel={(event) => {
            event.preventDefault();
            if (!busy) setReview(false);
          }}
        >
          <div className="dialog-heading">
            <div>
              <p className="eyebrow">LOCAL ACCESS</p>
              <h2>
                Review {chosen.length} account{chosen.length === 1 ? "" : "s"}
              </h2>
            </div>
            <button
              type="button"
              className="icon-button"
              aria-label="Close review"
              disabled={busy}
              onClick={() => setReview(false)}
            >
              <X size={20} />
            </button>
          </div>
          <div className="dialog-form">
            <p className="muted">
              Selected GHL staff will receive local GHL Connect access. Choose
              their local team, role, and duty explicitly. This does not change
              GHL.
            </p>
            <ul className="review-list">
              {chosen.map((person) => (
                <li key={person.id}>
                  {person.name}
                  <small>{person.email}</small>
                </li>
              ))}
            </ul>
            <fieldset>
              <legend>Local teams</legend>
              <div className="checkbox-list">
                {teams.map((team) => (
                  <label key={team.id}>
                    <input
                      type="checkbox"
                      checked={teamIds.includes(team.id)}
                      onChange={(event) =>
                        setTeamIds((ids) =>
                          event.target.checked
                            ? [...ids, team.id]
                            : ids.filter((id) => id !== team.id),
                        )
                      }
                    />
                    {team.name}
                  </label>
                ))}
              </div>
            </fieldset>
            {!teams.length && (
              <p className="field-hint">
                Create a local team before adding GHL staff.
              </p>
            )}
            <div className="two-fields">
              <label>
                Local role
                <select
                  value={role}
                  onChange={(event) =>
                    setRole(event.target.value as ProvisionRole)
                  }
                >
                  <option value="member">Member</option>
                  <option value="lead">Team lead</option>
                </select>
              </label>
              <label>
                Duty
                <select
                  value={duty}
                  onChange={(event) => setDuty(event.target.value as Duty)}
                >
                  <option value="other">Other</option>
                  <option value="opener">Opener</option>
                  <option value="closer">Closer</option>
                </select>
              </label>
            </div>
            {error && (
              <p className="field-error" role="alert">
                {error}
              </p>
            )}
            <div className="dialog-actions">
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => setReview(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || !chosen.length || !teamIds.length}
                onClick={() => void provision()}
              >
                {busy ? "Adding…" : `Add ${chosen.length} to GHL Connect`}
              </button>
            </div>
          </div>
        </dialog>
      )}
      {linkReview && (
        <LinkReviewDialog
          source={linkReview.source}
          target={linkReview.target}
          teams={teams}
          locations={
            directory?.locations
              .filter((location) =>
                linkReview.source.locationIds.includes(location.id),
              )
              .map((location) => location.name) ?? []
          }
          busy={busy}
          error={error}
          onClose={() => setLinkReview(null)}
          onConfirm={() => void link()}
        />
      )}
    </section>
  );
}

function LinkReviewDialog({
  source,
  target,
  teams,
  locations,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  source: DirectoryStaff;
  target: User;
  teams: Team[];
  locations: string[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    trigger.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    ref.current?.showModal();
    return () => {
      ref.current?.close();
      window.setTimeout(
        () => trigger.current?.isConnected && trigger.current.focus(),
        0,
      );
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="desk-dialog directory-dialog"
      aria-label="Review existing account link"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <div className="dialog-heading">
        <div>
          <p className="eyebrow">REVIEW ACCOUNT LINK</p>
          <h2>Link existing account</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Close review"
          disabled={busy}
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>
      <div className="dialog-form">
        <p>
          <strong>GHL staff:</strong> {source.name} · {source.email}
        </p>
        <p>
          <strong>Local account:</strong> {target.name} · {target.email}
        </p>
        <p>
          <strong>Local role and duty:</strong> {target.role} · {target.duty}
        </p>
        <p>
          <strong>Local teams:</strong>{" "}
          {target.teamIds
            .map((id) => teams.find((team) => team.id === id)?.name ?? id)
            .join(", ") || "None"}
        </p>
        <p>
          <strong>Staged GHL locations:</strong>{" "}
          {locations.join(", ") || "None listed"}. The server applies its
          approved locations when linking.
        </p>
        <p className="field-hint">
          The exact email match identifies a candidate only. Confirm this person
          and their existing access before linking. Local role, duty, teams, and
          password stay as they are.
        </p>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="button" disabled={busy} onClick={onConfirm}>
            {busy ? "Linking…" : "Confirm account link"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
